import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Calendar, Clock, CheckCircle, XCircle, RotateCcw, Target,
  Search, TrendingUp, UserCheck, Phone, ArrowRight, AlertTriangle,
  Eye, CalendarDays, MapPin, MessageSquare, FileText, Ban, User,
  BarChart3, Play, ClipboardList, DollarSign, PhoneCall, Plus,
  Shield, Bell, Send, RefreshCw, Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip, CockpitFilterBar, CockpitSearch } from "@/components/cockpit/CockpitShell";
import { format, isToday, isTomorrow, isFuture, isPast, parseISO, addDays } from "date-fns";
import { toast } from "sonner";
import { QuoteService } from "@/services/quote.service";
import { LeadService } from "@/services/lead.service";
import { FollowUpTaskService } from "@/services/follow-up-task.service";
import { AppointmentResultService } from "@/services/appointment-result.service";
import type { Opportunity, OpportunityStage, FollowUpTask } from "@/data/opportunityTypes";
import { errorPersistenceLog, startPersistenceLog, successPersistenceLog } from "@/lib/persistenceLogger";
import { isUuidLike, resolveProfileDisplayName } from "@/lib/profileDisplayName";
import { fetchProfileDisplayNames } from "@/lib/profileLookupCache";
import { findRepAppointmentOverlap } from "@/lib/appointmentOverlap";
import { isUuid } from "@/lib/uuid";
import { APPOINTMENTS_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import {
  AppointmentPageService,
  type AppointmentPageSummary,
  type AppointmentPageTab,
} from "@/services/appointment-page.service";
import {
  calculateConversionFunnel, getRepConversionMetrics, getSourceConversionMetrics,
  scoreLeadConversion, TEMP_CONFIG,
  type ConversionFunnel, type RepConversionMetrics, type SourceConversionMetrics,
} from "@/data/conversionIntelligence";
import {
  getProtectionStatus, detectNoShow, getRecoveryActions, calculateRescheduleMetrics,
  RISK_CONFIG,
  type ProtectionStatus, type NoShowDetection, type RecoveryAction, type RescheduleMetrics,
} from "@/data/appointmentProtection";

/* ═══════ OUTCOME SYSTEM ═══════ */

import {
  type AppointmentOutcome,
  type UnifiedAppointment,
  getAllAppointments,
  getUpcomingAppointments,
  getCompletedAppointments,
  deriveOutcome,
} from "@/data/appointmentSelectors";

const OUTCOME_CONFIG: Record<AppointmentOutcome, { label: string; icon: React.ElementType; color: string; bg: string }> = {
  upcoming:      { label: "Upcoming",        icon: Clock,          color: "text-info",            bg: "bg-info/10 border-info/30" },
  needs_outcome: { label: "Awaiting Outcome", icon: AlertTriangle, color: "text-warning",          bg: "bg-warning/10 border-warning/30" },
  sold:          { label: "Sold",            icon: CheckCircle,    color: "text-success",         bg: "bg-success/10 border-success/30" },
  no_sale:       { label: "No Sale",         icon: XCircle,        color: "text-destructive",         bg: "bg-destructive/5 border-destructive/20" },
  rescheduled:   { label: "Rescheduled",     icon: RotateCcw,      color: "text-warning",           bg: "bg-warning/10 border-warning/30" },
  canceled:      { label: "Canceled",        icon: Ban,            color: "text-muted-foreground",    bg: "bg-muted border-muted" },
  no_show:       { label: "No Show",         icon: AlertTriangle,  color: "text-warning",          bg: "bg-warning/10 border-warning/30" },
};

/* ═══════ SALES QA CHECKLIST ═══════ */

interface SalesQAChecklist {
  presentationCompleted: boolean;
  pricingShown: boolean;
  objectionsLogged: boolean;
  decisionMakersPresent: boolean;
  needsAssessed: boolean;
  proposalReviewed: boolean;
}

const SALES_QA_ITEMS: { key: keyof SalesQAChecklist; label: string; description: string }[] = [
  { key: "presentationCompleted", label: "Presentation completed", description: "Full product presentation was delivered" },
  { key: "pricingShown", label: "Pricing presented", description: "All pricing options were reviewed with customer" },
  { key: "needsAssessed", label: "Needs assessment done", description: "Customer needs and requirements documented" },
  { key: "decisionMakersPresent", label: "Decision makers present", description: "All decision makers were present" },
  { key: "proposalReviewed", label: "Proposal reviewed", description: "Written proposal was reviewed and discussed" },
  { key: "objectionsLogged", label: "Objections documented", description: "Customer objections and concerns captured" },
];

const INITIAL_QA: SalesQAChecklist = {
  presentationCompleted: false, pricingShown: false, objectionsLogged: false,
  decisionMakersPresent: false, needsAssessed: false, proposalReviewed: false,
};

/* ═══════ GUIDED NEXT STEPS ═══════ */

interface GuidedNextStep {
  label: string;
  description: string;
  action: "follow_up" | "reschedule" | "escalate" | "close" | "review";
  icon: React.ElementType;
  priority: "high" | "medium" | "low";
}

function getGuidedNextSteps(outcome: AppointmentOutcome, qa: SalesQAChecklist): GuidedNextStep[] {
  const steps: GuidedNextStep[] = [];
  if (outcome === "sold") {
    steps.push({ label: "Submit for build review", description: "Move to pre-construction review", action: "review", icon: ClipboardList, priority: "high" });
    steps.push({ label: "Send confirmation to customer", description: "Confirm the sale and next steps", action: "close", icon: Send, priority: "medium" });
  } else if (outcome === "no_sale") {
    if (!qa.pricingShown) {
      steps.push({ label: "Schedule pricing follow-up", description: "Customer didn't see full pricing — revisit", action: "follow_up", icon: DollarSign, priority: "high" });
    }
    if (!qa.decisionMakersPresent) {
      steps.push({ label: "Reschedule with decision maker", description: "Key decision maker was absent", action: "reschedule", icon: RotateCcw, priority: "high" });
    }
    steps.push({ label: "Create follow-up opportunity", description: "Keep this lead warm for recovery", action: "follow_up", icon: PhoneCall, priority: "high" });
    if (qa.objectionsLogged) {
      steps.push({ label: "Escalate to manager", description: "Share objections for coaching review", action: "escalate", icon: ArrowRight, priority: "medium" });
    }
  } else if (outcome === "no_show") {
    steps.push({ label: "Trigger recovery sequence", description: "Auto-send reschedule message", action: "follow_up", icon: RefreshCw, priority: "high" });
    steps.push({ label: "Reschedule appointment", description: "Book new time with customer", action: "reschedule", icon: RotateCcw, priority: "high" });
  } else if (outcome === "rescheduled") {
    steps.push({ label: "Confirm new time", description: "Send confirmation for rescheduled appointment", action: "close", icon: Send, priority: "high" });
  } else if (outcome === "canceled") {
    steps.push({ label: "Create recovery follow-up", description: "Attempt to re-engage this lead", action: "follow_up", icon: PhoneCall, priority: "medium" });
  }
  return steps;
}

/** Flattened enriched appointment for backward-compatible property access */
type EnrichedAppointment = Opportunity & {
  outcome: AppointmentOutcome;
  isHighValue: boolean;
  needsFollowUp: boolean;
  hasConversation: boolean;
  hasConsultationSession: boolean;
  temperature: import("@/data/conversionIntelligence").LeadTemperature;
  conversionScore: number;
  protection: ProtectionStatus;
  noShowDetection: NoShowDetection | null;
  recoveryActions: RecoveryAction[];
};

/* ═══════ MAIN COMPONENT ═══════ */

export default function AppointmentsPage() {
  const { salesOpportunities, salesOpportunitiesLoading, setSalesOpportunities, conversations, messages, followUpTasks, setFollowUpTasks, consultationSessions, appointmentResults, refreshData, lastRefreshedAt } = useAppData();
  const { role } = useRole();
  const { repName } = useCurrentRep();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [repFilter, setRepFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("upcoming");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [outcomeDialog, setOutcomeDialog] = useState<string | null>(null);
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [selectedOutcome, setSelectedOutcome] = useState<AppointmentOutcome>("no_sale");
  const [outcomeStep, setOutcomeStep] = useState<1 | 2 | 3 | 4>(1); // 1=outcome, 2=notes, 3=QA, 4=next steps
  const [salesQA, setSalesQA] = useState<SalesQAChecklist>({ ...INITIAL_QA });
  const [followUpDialog, setFollowUpDialog] = useState<string | null>(null);
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [followUpReason, setFollowUpReason] = useState("Post-appointment check-in");
  const [rescheduleDialog, setRescheduleDialog] = useState<string | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("10:00");
  const [initialLoading, setInitialLoading] = useState(true);
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [serverAppointmentsByTab, setServerAppointmentsByTab] = useState<Record<AppointmentPageTab, Opportunity[]>>({
    upcoming: [],
    completed: [],
  });
  const [serverCounts, setServerCounts] = useState<Record<AppointmentPageTab, number>>({
    upcoming: 0,
    completed: 0,
  });
  const [serverSummary, setServerSummary] = useState<AppointmentPageSummary | null>(null);
  const [serverLoading, setServerLoading] = useState(false);
  const [serverPageByTab, setServerPageByTab] = useState<Record<AppointmentPageTab, number>>({
    upcoming: 0,
    completed: 0,
  });
  const [serverPageSize, setServerPageSize] = useState(20);
  const [serverRepOptions, setServerRepOptions] = useState<string[]>([]);
  const initialRefreshCheckedRef = useRef(false);

  const serverTab = activeTab === "upcoming" || activeTab === "completed"
    ? activeTab as AppointmentPageTab
    : null;
  const serverPagingActive = APPOINTMENTS_SERVER_PAGING_ENABLED && serverTab !== null;

  useEffect(() => {
    if (initialRefreshCheckedRef.current) return;
    let cancelled = false;
    const run = async () => {
      const refreshedAt = lastRefreshedAt ? Date.parse(lastRefreshedAt) : 0;
      if (!refreshedAt || Number.isNaN(refreshedAt)) {
        if (!cancelled) setInitialLoading(false);
        return;
      }
      initialRefreshCheckedRef.current = true;
      const isStale = (Date.now() - refreshedAt) > 60_000;
      if (isStale) {
        void refreshData();
      }
      if (!cancelled) setInitialLoading(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [refreshData, lastRefreshedAt]);

  const isRepRole = role === "sales";
  const shouldScopeToOwnRep = isRepRole;
  const currentRep = shouldScopeToOwnRep ? String(repName || "").trim() : null;
  const normalizedCurrentRep = String(currentRep ?? "").trim().toLowerCase();
  const serverRepFilter = shouldScopeToOwnRep
    ? String(currentRep ?? "").trim()
    : (repFilter !== "all" ? repFilter : "");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setServerPageByTab((prev) => (
      prev.upcoming === 0 && prev.completed === 0
        ? prev
        : { upcoming: 0, completed: 0 }
    ));
  }, [debouncedSearch, serverRepFilter, serverPageSize]);

  const activeServerPage = serverTab ? serverPageByTab[serverTab] ?? 0 : 0;

  useEffect(() => {
    if (!serverPagingActive || !serverTab) return;
    let cancelled = false;
    const loadServerPage = async () => {
      setServerLoading(true);
      const pageResult = await AppointmentPageService.getPage({
        tab: serverTab,
        page: activeServerPage,
        pageSize: serverPageSize,
        search: debouncedSearch,
        rep: serverRepFilter,
      });
      if (cancelled) return;
      setServerAppointmentsByTab((prev) => ({ ...prev, [serverTab]: pageResult.data }));
      setServerCounts((prev) => ({ ...prev, [serverTab]: pageResult.count }));
      setServerLoading(false);
    };

    void loadServerPage().catch((error) => {
      console.warn("Failed to load server-paged appointments:", error);
      if (!cancelled) setServerLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [serverPagingActive, serverTab, activeServerPage, serverPageSize, debouncedSearch, serverRepFilter]);

  useEffect(() => {
    if (!APPOINTMENTS_SERVER_PAGING_ENABLED) return;
    let cancelled = false;
    void AppointmentPageService.getSummary({
      search: debouncedSearch,
      rep: serverRepFilter,
    }).then((summaryResult) => {
      if (!cancelled) setServerSummary(summaryResult.data);
    }).catch((error) => {
      console.warn("Failed to load server appointment summary:", error);
      if (!cancelled) setServerSummary(null);
    });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, serverRepFilter]);

  useEffect(() => {
    if (!APPOINTMENTS_SERVER_PAGING_ENABLED) return;
    let cancelled = false;
    void AppointmentPageService.getRepOptions().then((result) => {
      if (!cancelled) setServerRepOptions(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

   // Conversion intelligence
   // ── Unified appointment dataset (single source of truth) ──
   const appointmentOpportunitySource = serverPagingActive && serverTab
     ? serverAppointmentsByTab[serverTab]
     : salesOpportunities;
   const unifiedAll = useMemo(() =>
     getAllAppointments({ salesOpportunities: appointmentOpportunitySource, conversations, messages, followUpTasks, consultationSessions }),
     [appointmentOpportunitySource, conversations, messages, followUpTasks, consultationSessions],
   );

  // Flatten UnifiedAppointment → EnrichedAppointment for backward-compatible property access
  const appointments: EnrichedAppointment[] = useMemo(() =>
    unifiedAll.map((u) => ({
      ...u.opportunity,
      outcome: u.outcome,
      isHighValue: u.isHighValue,
      needsFollowUp: u.needsFollowUp,
      hasConversation: u.hasConversation,
      hasConsultationSession: u.hasConsultationSession,
      temperature: u.temperature,
      conversionScore: u.conversionScore,
      protection: u.protection,
      noShowDetection: u.noShowDetection,
      recoveryActions: u.recoveryActions,
    })),
    [unifiedAll],
  );
  const appointmentDataLoading = serverPagingActive
    ? serverLoading && appointments.length === 0
    : initialLoading || (salesOpportunitiesLoading && appointments.length === 0);

  // Conversion intelligence (uses same source data)
  const conversionFunnel = useMemo(() =>
    calculateConversionFunnel(salesOpportunities, followUpTasks),
    [salesOpportunities, followUpTasks]
  );
  const repConversion = useMemo(() =>
    getRepConversionMetrics(salesOpportunities, followUpTasks, conversations, messages),
    [salesOpportunities, followUpTasks, conversations, messages]
  );
  const sourceConversion = useMemo(() =>
    getSourceConversionMetrics(salesOpportunities),
    [salesOpportunities]
  );
  const rescheduleMetrics = useMemo(() =>
    calculateRescheduleMetrics(salesOpportunities, followUpTasks),
    [salesOpportunities, followUpTasks]
  );

  const reps = useMemo(() => {
    if (serverPagingActive && serverRepOptions.length > 0) {
      return serverRepOptions;
    }
    const s = new Set(
      appointments
        .map((a) => String(a.assignedRep ?? "").trim())
        .filter(Boolean),
    );
    return Array.from(s).sort();
  }, [appointments, serverPagingActive, serverRepOptions]);

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          [
            ...appointments.map((a) => String(a.assignedRep ?? "").trim()),
            ...serverRepOptions.map((rep) => String(rep ?? "").trim()),
          ]
            .filter((v) => isUuidLike(v)),
        ),
      );
      if (repIds.length === 0) {
        setRepNameById(new Map());
        return;
      }
      const map = await fetchProfileDisplayNames(repIds);
      if (cancelled) return;
      setRepNameById(map);
    };
    void loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [appointments, serverRepOptions]);

  const resolveRepLabel = (rep: string) => resolveProfileDisplayName(rep, repNameById, "Unassigned");
  const repMatchesCurrent = (rep: string) =>
    normalizedCurrentRep.length > 0 &&
    resolveRepLabel(rep).trim().toLowerCase() === normalizedCurrentRep;
  const hasScopedRepData = useMemo(
    () =>
      normalizedCurrentRep.length > 0 &&
      appointments.some((a) => repMatchesCurrent(String(a.assignedRep ?? ""))),
    [appointments, normalizedCurrentRep, repNameById],
  );
  const effectiveScopeToOwnRep = shouldScopeToOwnRep && hasScopedRepData;
  const isAwaitingOutcome = (appt: EnrichedAppointment) => {
    if (appt.outcome === "needs_outcome") return true;
    if (appt.outcome !== "upcoming" || !appt.appointmentTime) return false;
    const ts = new Date(appt.appointmentTime).getTime();
    return Number.isFinite(ts) && ts < Date.now();
  };

  const baseFiltered = useMemo(() => {
    let result = appointments;
    if (serverPagingActive) return result;
    if (effectiveScopeToOwnRep && normalizedCurrentRep) {
      result = result.filter((a) => repMatchesCurrent(String(a.assignedRep ?? "")));
    }
    if (repFilter !== "all") result = result.filter((a) => a.assignedRep === repFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((a) =>
        String(a.primaryName ?? "").toLowerCase().includes(q) ||
        String(a.projectType ?? "").toLowerCase().includes(q) ||
        String(a.assignedRep ?? "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [appointments, serverPagingActive, search, repFilter, effectiveScopeToOwnRep, normalizedCurrentRep, repNameById]);

  const upcoming = useMemo(() =>
    baseFiltered.filter((a) => a.outcome === "upcoming" || isAwaitingOutcome(a))
      .sort((a, b) => {
        if (isAwaitingOutcome(a) && !isAwaitingOutcome(b)) return -1;
        if (isAwaitingOutcome(b) && !isAwaitingOutcome(a)) return 1;
        return new Date(a.appointmentTime || a.createdAt).getTime() - new Date(b.appointmentTime || b.createdAt).getTime();
      }),
    [baseFiltered]);

  const completed = useMemo(() =>
    baseFiltered.filter((a) => a.outcome !== "upcoming" && !isAwaitingOutcome(a))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [baseFiltered]);
  const upcomingPagination = usePagination(upcoming, 20);
  const completedPagination = usePagination(completed, 20);
  const createServerPagination = useCallback((tab: AppointmentPageTab) => {
    const totalItems = serverCounts[tab] ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / serverPageSize));
    const page = Math.min(serverPageByTab[tab] ?? 0, totalPages - 1);
    return {
      paged: tab === "upcoming" ? upcoming : completed,
      page,
      totalPages,
      totalItems,
      hasNext: page < totalPages - 1,
      hasPrev: page > 0,
      next: () => setServerPageByTab((prev) => ({ ...prev, [tab]: Math.min((prev[tab] ?? 0) + 1, totalPages - 1) })),
      prev: () => setServerPageByTab((prev) => ({ ...prev, [tab]: Math.max((prev[tab] ?? 0) - 1, 0) })),
      reset: () => setServerPageByTab((prev) => ({ ...prev, [tab]: 0 })),
      pageStart: totalItems === 0 ? 0 : page * serverPageSize + 1,
      pageEnd: Math.min((page + 1) * serverPageSize, totalItems),
      pageSize: serverPageSize,
      setPageSize: (size: number) => {
        setServerPageSize(size);
        setServerPageByTab({ upcoming: 0, completed: 0 });
      },
    };
  }, [completed, serverCounts, serverPageByTab, serverPageSize, upcoming]);
  const upcomingPage = serverPagingActive ? createServerPagination("upcoming") : upcomingPagination;
  const completedPage = serverPagingActive ? createServerPagination("completed") : completedPagination;
  useEffect(() => {
    upcomingPagination.reset();
    completedPagination.reset();
    setServerPageByTab({ upcoming: 0, completed: 0 });
  }, [search, repFilter, activeTab]);

  const clientStats = useMemo(() => {
    const all = appointments;
    const todayCount = all.filter((a) => a.appointmentTime && isToday(parseISO(a.appointmentTime))).length;
    const tomorrowCount = all.filter((a) => a.appointmentTime && isTomorrow(parseISO(a.appointmentTime))).length;
    const upcomingCount = all.filter((a) => a.outcome === "upcoming").length;
    const needsOutcome = all.filter((a) => isAwaitingOutcome(a)).length;
    const soldCount = all.filter((a) => a.outcome === "sold").length;
    const completedAll = all.filter((a) => a.outcome !== "upcoming" && !isAwaitingOutcome(a));
    const closeRate = completedAll.length > 0 ? Math.round((soldCount / completedAll.length) * 100) : 0;
    const noShowCount = all.filter((a) => a.outcome === "no_show").length;
    const autoDetectedNoShows = all.filter((a) => a.noShowDetection?.isNoShow).length;
    const atRiskCount = all.filter((a) => a.outcome === "upcoming" && (a.protection.noShowRisk === "high" || a.protection.noShowRisk === "critical")).length;
    const protectedCount = all.filter((a) => a.outcome === "upcoming" && a.protection.isProtected).length;
    const showRate = completedAll.length > 0 ? Math.round(((completedAll.length - noShowCount) / completedAll.length) * 100) : 100;
    const needsFollowUp = all.filter((a) => a.needsFollowUp).length;
    const highValue = all.filter((a) => a.isHighValue && a.outcome === "upcoming").length;
    return { todayCount, tomorrowCount, upcomingCount, soldCount, closeRate, showRate, total: all.length, noShowCount, completedCount: completedAll.length, needsOutcome, needsFollowUp, highValue, autoDetectedNoShows, atRiskCount, protectedCount };
  }, [appointments]);
  const stats = useMemo(() => {
    if (!serverPagingActive || !serverSummary) return clientStats;
    return {
      ...clientStats,
      total: serverSummary.total,
      todayCount: serverSummary.todayCount,
      tomorrowCount: serverSummary.tomorrowCount,
      upcomingCount: serverSummary.upcomingCount,
      completedCount: serverSummary.completedCount,
      soldCount: serverSummary.soldCount,
      noShowCount: serverSummary.noShowCount,
      needsOutcome: serverSummary.needsOutcome,
      highValue: serverSummary.highValue,
      closeRate: serverSummary.closeRate,
      showRate: serverSummary.showRate,
    };
  }, [clientStats, serverPagingActive, serverSummary]);

  const assistiveSeed = useMemo(() => {
    const pool = (upcoming.length > 0 ? upcoming : baseFiltered).filter(
      (a) => String(a.leadSource ?? "").trim().length > 0 || String(a.zip ?? "").trim().length > 0,
    );
    if (pool.length === 0) {
      return { leadZip: "", leadSource: "", leadQualityScore: 65, hasData: false };
    }

    // Deterministic seed: pick most frequent lead source (then alpha tie-break),
    // then most frequent ZIP within that source (then alpha tie-break).
    const sourceCounts = new Map<string, number>();
    for (const a of pool) {
      const src = String(a.leadSource ?? "").trim();
      if (!src) continue;
      sourceCounts.set(src, (sourceCounts.get(src) ?? 0) + 1);
    }
    const sortedSources = Array.from(sourceCounts.entries()).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0].localeCompare(b[0]);
    });
    const leadSource = sortedSources[0]?.[0] ?? "";

    const sourcePool = leadSource
      ? pool.filter((a) => String(a.leadSource ?? "").trim() === leadSource)
      : pool;
    const zipCounts = new Map<string, number>();
    for (const a of sourcePool) {
      const z = String(a.zip ?? "").trim();
      if (!z) continue;
      zipCounts.set(z, (zipCounts.get(z) ?? 0) + 1);
    }
    const sortedZips = Array.from(zipCounts.entries()).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0].localeCompare(b[0]);
    });
    const leadZip = sortedZips[0]?.[0] ?? "";

    const scoreBase = sourcePool.length > 0 ? sourcePool : pool;
    const leadQualityScore = Math.round(
      scoreBase.reduce((sum, a) => sum + (typeof a.conversionScore === "number" ? a.conversionScore : 65), 0) /
      scoreBase.length,
    );

    return { leadZip, leadSource, leadQualityScore, hasData: Boolean(leadSource) };
  }, [baseFiltered, upcoming]);

  const dbAssistive = useMemo(() => {
    if (!assistiveSeed.hasData) return null;
    const repByOppId = new Map<string, string>();
    for (const r of appointmentResults) {
      const oppId = String(r.opportunityId ?? "").trim();
      const rep = String(r.assignedRep ?? "").trim();
      if (oppId && rep && !repByOppId.has(oppId)) repByOppId.set(oppId, rep);
    }
    const pickRepLabel = (...candidates: Array<string | null | undefined>) => {
      for (const raw of candidates) {
        const value = String(raw ?? "").trim();
        if (!value) continue;
        if (!isUuidLike(value)) return value;
      }
      for (const raw of candidates) {
        const value = String(raw ?? "").trim();
        if (!value) continue;
        const resolved = resolveRepLabel(value);
        if (resolved !== "Unassigned") return resolved;
      }
      return "Unassigned";
    };

    const byRep = new Map<string, {
      rep: string;
      total: number;
      won: number;
      sourceTotal: number;
      sourceWon: number;
      zipTotal: number;
      zipWon: number;
      futureSlots: Date[];
    }>();

    const opportunityPool = effectiveScopeToOwnRep && normalizedCurrentRep
      ? salesOpportunities.filter((o) => repMatchesCurrent(String(o.assignedRep ?? "")))
      : salesOpportunities;

    for (const opp of opportunityPool) {
      const oppId = String(opp.id ?? "").trim();
      const repFromResult = oppId ? String(repByOppId.get(oppId) ?? "").trim() : "";
      const rep = pickRepLabel(
        opp.assignedRep,
        repFromResult,
      );

      if (!byRep.has(rep)) {
        byRep.set(rep, {
          rep,
          total: 0,
          won: 0,
          sourceTotal: 0,
          sourceWon: 0,
          zipTotal: 0,
          zipWon: 0,
          futureSlots: [],
        });
      }
      const m = byRep.get(rep)!;
      m.total += 1;
      if (opp.stage === "Closed Won") m.won += 1;

      if (String(opp.leadSource ?? "").trim() === assistiveSeed.leadSource) {
        m.sourceTotal += 1;
        if (opp.stage === "Closed Won") m.sourceWon += 1;
      }
      if (assistiveSeed.leadZip && String(opp.zip ?? "").trim() === assistiveSeed.leadZip) {
        m.zipTotal += 1;
        if (opp.stage === "Closed Won") m.zipWon += 1;
      }

      if (opp.appointmentTime) {
        const d = new Date(opp.appointmentTime);
        if (Number.isFinite(d.getTime()) && d > new Date()) m.futureSlots.push(d);
      }
    }

    const suggestions = Array.from(byRep.values())
      .map((m) => {
        const overallRate = m.total ? Math.round((m.won / m.total) * 100) : 0;
        const sourceRate = m.sourceTotal ? Math.round((m.sourceWon / m.sourceTotal) * 100) : 0;
        const zipRate = m.zipTotal ? Math.round((m.zipWon / m.zipTotal) * 100) : 0;
        const fitScore = Math.round(overallRate * 0.45 + sourceRate * 0.4 + zipRate * 0.15);

        const slots = [...m.futureSlots].sort((a, b) => a.getTime() - b.getTime());
        const t1 = slots[0] ? format(slots[0], "h:mm a") : "TBD";
        const t2 = slots[1] ? format(slots[1], "h:mm a") : t1;

        return {
          rep: m.rep,
          fitScore,
          fitLabel: fitScore >= 75 ? "Best Option" : fitScore >= 50 ? "Good Option" : "Lower Fit",
          timeWindow: slots.length ? `${t1} – ${t2}` : "Schedule TBD",
          sourceRate,
          overallRate,
        };
      })
      .sort((a, b) => b.fitScore - a.fitScore)
      .slice(0, 4);

    return {
      leadSource: assistiveSeed.leadSource,
      leadZip: assistiveSeed.leadZip || "—",
      leadQuality: assistiveSeed.leadQualityScore >= 75 ? "High" : assistiveSeed.leadQualityScore >= 50 ? "Medium" : "Low",
      bestMatch: resolveRepLabel(suggestions[0]?.rep ?? ""),
      suggestions,
    };
  }, [assistiveSeed, salesOpportunities, appointmentResults, repNameById, effectiveScopeToOwnRep, normalizedCurrentRep]);

  const detailAppt = detailId ? appointments.find((a) => a.id === detailId) : null;
  const detailConvo = detailAppt ? conversations.find((c) => c.linkedLeadId === detailAppt.id || c.linkedQuoteId === detailAppt.id) : null;
  const detailMessages = detailConvo ? messages.filter((m) => m.conversationId === detailConvo.id).slice(-5) : [];

  const findOpportunityById = useCallback((id: string | null | undefined) => {
    const targetId = String(id ?? "").trim();
    if (!targetId) return null;
    return (
      salesOpportunities.find((o) => o.id === targetId) ??
      serverAppointmentsByTab.upcoming.find((o) => o.id === targetId) ??
      serverAppointmentsByTab.completed.find((o) => o.id === targetId) ??
      appointments.find((o) => o.id === targetId) ??
      null
    );
  }, [appointments, salesOpportunities, serverAppointmentsByTab]);

  const patchOpportunityState = useCallback((id: string, patch: (opp: Opportunity) => Opportunity) => {
    const patchList = (rows: Opportunity[]) => rows.map((o) => (o.id === id ? patch(o) : o));
    setSalesOpportunities((prev) => patchList(prev));
    setServerAppointmentsByTab((prev) => ({
      upcoming: patchList(prev.upcoming),
      completed: patchList(prev.completed),
    }));
  }, [setSalesOpportunities]);

  /* ═══════ ACTIONS ═══════ */
  async function persistOpportunityPatch(
    opp: Opportunity,
    updates: {
      stage?: OpportunityStage;
      appointmentTime?: string;
      lostReason?: string;
      wonAt?: string;
      lostAt?: string;
      updatedAt?: string;
      activities?: Opportunity["activities"];
    },
  ) {
    const quoteId = String(opp.quoteId ?? "").trim();
    const leadId = String(opp.leadId ?? "").trim() || String(opp.id ?? "").trim();

   if (quoteId) {
      try {
        await QuoteService.update(quoteId, {
          status: updates.stage,
          lost_reason: updates.lostReason ?? null,
          won_at: updates.wonAt ?? null,
          lost_at: updates.lostAt ?? null,
        });
      } catch (err) {
        console.warn("Quote update skipped; quote row not found or not required for appointment update:", err);
      }
    }

    if (leadId) {
      const { error } = await LeadService.update(leadId, {
        stage: updates.stage,
        appointment_time: updates.appointmentTime,
        activities: (updates.activities ?? undefined) as unknown[] | undefined,
      });

      if (error) throw error;
    }
  }

   async function resolveLeadCustomerId(opp: Opportunity): Promise<string | null> {
      const oppWithCustomer = opp as Opportunity & { customerId?: string };
      if (oppWithCustomer.customerId) return String(oppWithCustomer.customerId);
      const { data, error } = await LeadService.getById(opp.leadId as string);
      if (error || !data) return null;
      return String(((data as { customer_id?: string | null }).customer_id) ?? "");
    }


  async function ensureQuoteForOpportunity(opp: Opportunity): Promise<string | null> {
    const leadId = String(opp.leadId ?? opp.id ?? "").trim();
    if (!leadId) return null;

    const existingQuoteId = String(opp.quoteId ?? "").trim();

    if (existingQuoteId) {
      try {
        const { data } = await QuoteService.getById(existingQuoteId);
        if (data?.id) return String(data.id);
      } catch (err) {
        console.warn("Existing quote invalid, recreating quote for appointment flow:", err);
      }
    }

    const customerId = await resolveLeadCustomerId(opp);
    const { data, error } = await QuoteService.upsertByLeadId(leadId, {
      quote_name: opp.primaryName || "Consultation Quote",
      customer_name: opp.primaryName || "",
      customer_email: opp.primaryEmail || null,
      customer_phone: opp.primaryPhone || null,
      customer_address: [opp.address, opp.city, [opp.state, opp.zip].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(", "),
      customer_id: customerId || null,
      lead_source: opp.leadSource || null,
      status: "Appointment Scheduled",
      notes: null,
      total_estimate: opp.totalEstimate || 0,
    });

    if (error || !data?.id) {
      console.error("Failed to ensure quote for appointment flow:", error);
      return null;
    }

    return String(data.id);
  }

  async function handleMarkOutcome() {
    if (!outcomeDialog) return;
    if (!outcomeNotes.trim()) { toast.error("Notes are required"); return; }

    const qaComplete = Object.values(salesQA).filter(Boolean).length;
    const qaTotal = SALES_QA_ITEMS.length;

    const stageMap: Partial<Record<AppointmentOutcome, OpportunityStage>> = {
      sold: "Closed Won", no_sale: "Closed Lost", rescheduled: "Appointment Scheduled", canceled: "Closed Lost", no_show: "Closed Lost",
    };
    const newStage = stageMap[selectedOutcome];
    if (!newStage) return;
    const target = findOpportunityById(outcomeDialog);
    if (!target) return;
    const correlationId = startPersistenceLog("appointment_mark_outcome", {
      entityType: "opportunity",
      entityId: target.id,
      outcome: selectedOutcome,
      route: "/appointments",
    });
    const now = new Date().toISOString();
    const nextActivities: Opportunity["activities"] = [
      ...target.activities,
      {
        id: `act-${Date.now()}`, type: "note" as const,
        description: `Appointment outcome: ${OUTCOME_CONFIG[selectedOutcome].label} — ${outcomeNotes}`,
        createdAt: now, createdBy: currentRep || "Admin",
      },
      {
        id: `act-${Date.now() + 1}`, type: "note" as const,
        description: `Sales QA: ${qaComplete}/${qaTotal} completed${!salesQA.presentationCompleted ? " (presentation incomplete)" : ""}${!salesQA.pricingShown ? " (pricing not shown)" : ""}`,
        createdAt: now, createdBy: currentRep || "Admin",
      },
    ];

    try {
      await persistOpportunityPatch(target, {
        stage: newStage,
        updatedAt: now,
        wonAt: selectedOutcome === "sold" ? now : undefined,
        lostAt: selectedOutcome !== "sold" && selectedOutcome !== "rescheduled" ? now : undefined,
        lostReason: selectedOutcome !== "sold" && selectedOutcome !== "rescheduled" ? selectedOutcome : undefined,
        activities: nextActivities,
      });

      const ensuredQuoteId = await ensureQuoteForOpportunity(target);

      const { error: apptError } = await AppointmentResultService.upsert({
        appointmentId: target.id,
        leadId: String(target.leadId ?? "").trim() || String(target.id ?? "").trim(),
        scheduledAt: target.appointmentTime ?? now,
        status:
          selectedOutcome === "rescheduled" ? "rescheduled" :
          selectedOutcome === "canceled" ? "cancelled" :
          selectedOutcome === "no_show" ? "no_show" : "completed",
        result:
          selectedOutcome === "sold" ? "booked" :
          selectedOutcome === "no_sale" ? "not_interested" :
          selectedOutcome === "rescheduled" ? "deferred" : null,
        notes: outcomeNotes,
        createdBy: currentRep || "Admin",
        noSaleReason: selectedOutcome === "no_sale" ? outcomeNotes : undefined,
        noDemoReason: selectedOutcome === "no_show" ? outcomeNotes : undefined,
        rescheduleDatetime: selectedOutcome === "rescheduled" ? target.appointmentTime : undefined,
      });
      if (apptError) throw apptError;

      patchOpportunityState(outcomeDialog, (o) => ({
        ...o, stage: newStage, updatedAt: now,
        ...(selectedOutcome === "sold" && { wonAt: now }),
        ...(selectedOutcome !== "sold" && selectedOutcome !== "rescheduled" && { lostAt: now, lostReason: selectedOutcome }),
        activities: nextActivities,
        ...(ensuredQuoteId ? { quoteId: ensuredQuoteId } : {}),
      }));

      toast.success(`Marked as ${OUTCOME_CONFIG[selectedOutcome].label}`);
      // Show guided next steps
      setOutcomeStep(4);
      successPersistenceLog("appointment_mark_outcome", correlationId, {
        entityType: "opportunity",
        entityId: target.id,
        outcome: selectedOutcome,
      });
    } catch (err) {
      console.error("Failed to persist appointment outcome:", err);
      errorPersistenceLog("appointment_mark_outcome", correlationId, err, {
        entityType: "opportunity",
        entityId: target.id,
      });
      toast.error("Failed to save appointment outcome to DB.");
    }
  }

  function handleOutcomeComplete() {
    if (selectedOutcome === "no_sale" || selectedOutcome === "no_show") {
      setFollowUpDialog(outcomeDialog);
    }
    resetOutcomeDialog();
  }

  function resetOutcomeDialog() {
    setOutcomeDialog(null);
    setOutcomeNotes("");
    setOutcomeStep(1);
    setSalesQA({ ...INITIAL_QA });
  }

  async function handleCancel(id: string) {
    const target = findOpportunityById(id);
    const now = new Date().toISOString();
    if (!target) return;
    const correlationId = startPersistenceLog("appointment_cancel", {
      entityType: "opportunity",
      entityId: id,
      route: "/appointments",
    });
    const nextActivities: Opportunity["activities"] = [
      ...target.activities,
      { id: `act-${Date.now()}`, type: "note" as const, description: "Appointment canceled", createdAt: now, createdBy: currentRep || "Admin" },
    ];
    try {
      await persistOpportunityPatch(target, {
        stage: "Closed Lost",
        updatedAt: now,
        lostAt: now,
        lostReason: "canceled",
        activities: nextActivities,
      });
      patchOpportunityState(id, (o) => ({
        ...o,
        stage: "Closed Lost" as OpportunityStage,
        updatedAt: now,
        lostReason: "canceled",
        activities: nextActivities,
      }));
      successPersistenceLog("appointment_cancel", correlationId, {
        entityType: "opportunity",
        entityId: id,
      });
      toast.success("Appointment canceled");
    } catch (err) {
      console.error("Failed to persist cancellation:", err);
      errorPersistenceLog("appointment_cancel", correlationId, err, {
        entityType: "opportunity",
        entityId: id,
      });
      toast.error("Failed to cancel appointment in DB.");
    }
  }

  function openRescheduleDialog(id: string) {
    const target = findOpportunityById(id);
    if (!target) return;

    const current = target.appointmentTime
      ? parseISO(target.appointmentTime)
      : addDays(new Date(), 1);

    setRescheduleDialog(id);
    setRescheduleDate(format(current, "yyyy-MM-dd"));
    setRescheduleTime(format(current, "HH:mm"));
  }

  const rescheduleConflict = useMemo(() => {
    if (!rescheduleDialog || !rescheduleDate || !rescheduleTime) return null;
    const target = findOpportunityById(rescheduleDialog);
    if (!target) return null;
    const targetRep = String(target.assignedRep ?? "").trim();
    if (!targetRep || !isUuid(targetRep)) return null;
    const newTime = new Date(`${rescheduleDate}T${rescheduleTime}:00`).toISOString();
    const conflictPool = salesOpportunities.length > 0
      ? salesOpportunities
      : [...serverAppointmentsByTab.upcoming, ...serverAppointmentsByTab.completed];
    return findRepAppointmentOverlap(conflictPool, {
      rep: targetRep,
      candidateStartIso: newTime,
      excludeId: target.id,
    });
  }, [findOpportunityById, rescheduleDialog, rescheduleDate, rescheduleTime, salesOpportunities, serverAppointmentsByTab]);

  async function handleConfirmReschedule() {
    if (!rescheduleDialog || !rescheduleDate || !rescheduleTime) {
      toast.error("Please select a new date and time");
      return;
    }

    const target = findOpportunityById(rescheduleDialog);
    if (!target) return;
    const correlationId = startPersistenceLog("appointment_reschedule", {
      entityType: "opportunity",
      entityId: target.id,
      route: "/appointments",
    });

    const now = new Date().toISOString();
    const newTime = new Date(`${rescheduleDate}T${rescheduleTime}:00`).toISOString();
    const targetRep = String(target.assignedRep ?? "").trim();
    if (!isUuid(targetRep)) {
      toast.error("Assigned rep is invalid", {
        description: "Opportunity must be assigned to a valid rep UUID before rescheduling.",
      });
      return;
    }
    const conflictingAppointment = rescheduleConflict;
    if (conflictingAppointment) {
      toast.error("Scheduling conflict", {
        description: `${targetRep || "This rep"} already has an appointment within the 2-hour appointment + 2-hour buffer window.`,
      });
      return;
    }
    const nextActivities: Opportunity["activities"] = [
      ...target.activities,
      {
        id: `act-${Date.now()}`,
        type: "note" as const,
        description: `Appointment rescheduled to ${format(parseISO(newTime), "MMM d, h:mm a")}`,
        createdAt: now,
        createdBy: currentRep || "Admin",
      },
    ];

    try {
      await persistOpportunityPatch(target, {
        stage: "Appointment Scheduled",
        appointmentTime: newTime,
        updatedAt: now,
        activities: nextActivities,
      });

      const leadId = String(target.leadId ?? target.id).trim();
      const quoteId = await ensureQuoteForOpportunity(target);
      const { error: appointmentError } = await AppointmentResultService.upsert({
        appointmentId: target.id,
        leadId,
        scheduledAt: newTime,
        status: "scheduled",
        result: null,
        notes: `Appointment rescheduled to ${format(parseISO(newTime), "MMM d, h:mm a")}`,
        createdBy: currentRep || "Admin",
        contactName: target.primaryName,
        contactPhone: target.primaryPhone,
        contactEmail: target.primaryEmail || undefined,
        rescheduleDatetime: newTime,
      });

      if (appointmentError) throw appointmentError;

      if (quoteId) {
        patchOpportunityState(target.id, (o) => ({ ...o, quoteId }));
      }

      patchOpportunityState(target.id, (o) => ({
        ...o,
        appointmentTime: newTime,
        stage: "Appointment Scheduled" as OpportunityStage,
        updatedAt: now,
        activities: nextActivities,
      }));

      toast.success("Appointment rescheduled");
      setRescheduleDialog(null);
      setRescheduleDate("");
      setRescheduleTime("10:00");
    } catch (err) {
      console.error("Failed to persist reschedule:", err);
      errorPersistenceLog("appointment_reschedule", correlationId, err, {
        entityType: "opportunity",
        entityId: target.id,
      });
      toast.error("Reschedule failed to persist to DB.");
    }
  }

  async function handleCreateFollowUp() {
    if (!followUpDialog) return;
    const opp = findOpportunityById(followUpDialog);
    if (!opp) return;
    const correlationId = startPersistenceLog("appointment_create_followup", {
      entityType: "opportunity",
      entityId: opp.id,
      route: "/appointments",
    });
    const now = new Date();
    const defaultDisposition: FollowUpTask["disposition"] = {
      reason: "other",
      homeownerPresent: false,
      decisionMakerPresent: false,
      mainReasonNoDemoText: followUpReason || "Follow-up requested",
      pricingDiscussed: false,
      stillLiveOpportunity: true,
      followUpType: "rep",
      followUpNotes: followUpNotes || "",
      bestContactMethod: "call",
      completedAt: now.toISOString(),
      completedBy: currentRep || "Admin",
    };
    const task: FollowUpTask = {
      id: `FU-${Date.now()}`, opportunityId: opp.id, type: "rep" as const,
      assignedTo: opp.assignedRep || currentRep || "Admin",
      dueDate: addDays(now, 1).toISOString(), status: "pending" as const,
      notes: `${followUpReason}${followUpNotes ? ` — ${followUpNotes}` : ""}`,
      createdAt: now.toISOString(), disposition: defaultDisposition,
      customerName: opp.primaryName, customerPhone: opp.primaryPhone,
      priority: "high" as const, executionMethod: "call",
      dispositionReason: followUpReason,
    };
    const nextActivities: Opportunity["activities"] = [
      ...opp.activities,
      { id: `act-${Date.now()}`, type: "note" as const, description: `Follow-up created: ${followUpReason}`, createdAt: now.toISOString(), createdBy: currentRep || "Admin" },
    ];

    try {
      const { error: taskErr } = await FollowUpTaskService.upsert(task);
      if (taskErr) throw taskErr;

      await persistOpportunityPatch(opp, {
        stage: "Follow-Up Needed",
        updatedAt: now.toISOString(),
        activities: nextActivities,
      });

      setFollowUpTasks((prev) => [...prev, task]);
      patchOpportunityState(followUpDialog, (o) => ({
        ...o,
        stage: "Follow-Up Needed" as OpportunityStage,
        updatedAt: now.toISOString(),
        activities: nextActivities,
      }));

      successPersistenceLog("appointment_create_followup", correlationId, {
        entityType: "opportunity",
        entityId: opp.id,
        followUpTaskId: task.id,
      });
      toast.success("Follow-up created and pipeline updated");
      setFollowUpDialog(null);
      setFollowUpNotes("");
    } catch (err) {
      console.error("Failed to persist follow-up:", err);
      errorPersistenceLog("appointment_create_followup", correlationId, err, {
        entityType: "opportunity",
        entityId: opp.id,
      });
      toast.error("Failed to create follow-up in DB.");
    }
  }

  /* ═══════ RENDER ═══════ */

  return (
    <TooltipProvider>
      <CockpitPage>
        {/* ── Masthead ── */}
        <CockpitMasthead
          title="Appointment Command Center"
          right={
            <>
              <HeroMetric label="Today" value={stats.todayCount} hero />
              <HeroDivider />
              <HeroMetric label="Close Rate" value={`${stats.closeRate}%`} tone={stats.closeRate >= 50 ? "good" : stats.closeRate >= 30 ? "warn" : "bad"} />
              <HeroDivider />
              <HeroMetric label="Show Rate" value={`${stats.showRate}%`} tone={stats.showRate >= 90 ? "good" : stats.showRate >= 75 ? "warn" : "bad"} />
              {stats.needsOutcome > 0 && (
                <>
                  <HeroDivider />
                  <HeroMetric label="Awaiting Outcome" value={stats.needsOutcome} tone="warn" />
                </>
              )}
            </>
          }
        />

        {/* Primary Tabs Nav */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-auto p-0 bg-transparent rounded-none border-b border-border/60 w-fit">
            <TabsTrigger
              value="upcoming"
              className="gap-1.5 h-11 px-5 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
            >
              <Clock className="h-3.5 w-3.5" /> Upcoming
              {stats.upcomingCount > 0 && <Badge variant="secondary" className="h-6 min-w-[28px] px-2 text-[11px] ml-1">{stats.upcomingCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              className="gap-1.5 h-11 px-5 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
            >
              <CheckCircle className="h-3.5 w-3.5" /> Completed
              {stats.completedCount > 0 && <Badge variant="secondary" className="h-6 min-w-[28px] px-2 text-[11px] ml-1">{stats.completedCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger
              value="performance"
              className="gap-1.5 h-11 px-5 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
            >
              <BarChart3 className="h-3.5 w-3.5" /> Performance
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* KPI Strip (Upcoming + Completed only) */}
        {activeTab !== "performance" && (
          <CockpitKpiStrip
            items={
              activeTab === "completed"
                ? [
                    { label: "Completed", value: stats.completedCount },
                    { label: "Sold", value: stats.soldCount, tone: "good" },
                    { label: "Close Rate", value: `${stats.closeRate}%`, tone: stats.closeRate >= 50 ? "good" : stats.closeRate >= 30 ? "warn" : "bad" },
                    { label: "Show Rate", value: `${stats.showRate}%`, tone: stats.showRate >= 90 ? "good" : stats.showRate >= 75 ? "warn" : "bad" },
                    { label: "No Shows", value: stats.noShowCount, tone: stats.noShowCount > 0 ? "warn" : "default" },
                    { label: "Awaiting Outcome", value: stats.needsOutcome, tone: stats.needsOutcome > 0 ? "warn" : "default" },
                  ]
                : [
                    { label: "Today", value: stats.todayCount },
                    { label: "Tomorrow", value: stats.tomorrowCount },
                    { label: "Upcoming", value: stats.upcomingCount },
                    { label: "At Risk", value: stats.atRiskCount, tone: stats.atRiskCount > 0 ? "warn" : "default" },
                    { label: "Protected", value: stats.protectedCount, tone: "good" },
                    { label: "High Value", value: stats.highValue, tone: stats.highValue > 0 ? "good" : "default" },
                  ]
            }
          />
        )}

        {/* Protection & Urgency Alerts (Upcoming only) */}
        {activeTab === "upcoming" && (stats.needsOutcome > 0 || stats.needsFollowUp > 0 || stats.atRiskCount > 0 || stats.autoDetectedNoShows > 0) && (
          <div className="flex gap-2 flex-wrap">
            {stats.autoDetectedNoShows > 0 && (
              <div className="flex items-center gap-2 border border-border/60 rounded-md px-3 py-1.5 text-[12px]">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                <span className="text-foreground">{stats.autoDetectedNoShows} no-show{stats.autoDetectedNoShows > 1 ? "s" : ""} detected — recovery needed</span>
              </div>
            )}
            {stats.atRiskCount > 0 && (
              <div className="flex items-center gap-2 border border-border/60 rounded-md px-3 py-1.5 text-[12px]">
                <Shield className="h-3.5 w-3.5 text-warning" />
                <span className="text-foreground">{stats.atRiskCount} upcoming at high no-show risk</span>
              </div>
            )}
            {stats.needsOutcome > 0 && (
              <div className="flex items-center gap-2 border border-border/60 rounded-md px-3 py-1.5 text-[12px]">
                <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                <span className="text-foreground">{stats.needsOutcome} awaiting outcome</span>
              </div>
            )}
            {stats.needsFollowUp > 0 && (
              <div className="flex items-center gap-2 border border-border/60 rounded-md px-3 py-1.5 text-[12px]">
                <ClipboardList className="h-3.5 w-3.5 text-warning" />
                <span className="text-foreground">{stats.needsFollowUp} need follow-up</span>
              </div>
            )}
            {stats.highValue > 0 && (
              <div className="flex items-center gap-2 border border-border/60 rounded-md px-3 py-1.5 text-[12px]">
                <DollarSign className="h-3.5 w-3.5 text-primary" />
                <span className="text-foreground">{stats.highValue} high-value upcoming</span>
              </div>
            )}
            {stats.protectedCount > 0 && (
              <div className="flex items-center gap-2 border border-border/60 rounded-md px-3 py-1.5 text-[12px]">
                <Shield className="h-3.5 w-3.5 text-success" />
                <span className="text-foreground">{stats.protectedCount} protected</span>
              </div>
            )}
          </div>
        )}

        {/* ═══ SCHEDULING ASSIST (Upcoming only) ═══ */}
        {activeTab === "upcoming" && (
          <section className="space-y-3 pt-3 border-t border-border/60">
            <div className="flex items-center justify-between min-h-8">
              <div className="flex items-center gap-2">
                <Zap className="h-3.5 w-3.5 text-primary" />
                <p className="text-[12px] font-bold text-foreground uppercase tracking-wider">Recommended Appointment Options</p>
                <Badge variant="outline" className="text-[8px] px-1.5 py-0">Beta · Assistive</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground">Based on proximity, availability, route clustering & schedule density</p>
            </div>

            {/* Pre-Booking Insight */}
            {dbAssistive ? (() => {
              return (
                <div className="flex items-center gap-x-5 gap-y-2 flex-wrap py-2 border-b border-border/60">
                  <div className="flex items-center gap-1.5">
                    <Target className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Source:</span>
                    <span className="text-[11px] font-semibold">{dbAssistive.leadSource}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <UserCheck className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Quality:</span>
                    <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${
                      dbAssistive.leadQuality === "High" ? "bg-success/10 text-success border-success/20" :
                      dbAssistive.leadQuality === "Medium" ? "bg-warning/10 text-warning border-warning/20" :
                      "bg-destructive/10 text-destructive border-destructive/20"
                    }`}>{dbAssistive.leadQuality}</Badge>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Best match:</span>
                    <span className="text-[11px] font-semibold">{dbAssistive.bestMatch}</span>
                  </div>
                  {dbAssistive.bestMatch && (
                    <div className="flex items-center gap-1.5">
                      <ArrowRight className="h-3 w-3 text-primary" />
                      <span className="text-[10px] text-primary font-medium">Rep has nearby jobs — optimal routing</span>
                    </div>
                  )}
                </div>
              );
            })() : (
              <div className="py-2 border-b border-border/60 text-[11px] text-muted-foreground">
                Waiting for DB-backed appointment records with source and ZIP to generate recommendations.
              </div>
            )}

            {/* Booking Suggestions Grid */}
            {dbAssistive && dbAssistive.suggestions.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-1">
                {dbAssistive.suggestions.map((s, i) => (
                  <div key={i} className={`rounded-lg border p-3 space-y-2 transition-all ${
                    i === 0 ? "border-primary/30 bg-primary/5 ring-1 ring-primary/10" :
                    s.fitLabel === "Good Option" ? "bg-muted/20" :
                    "bg-muted/10 opacity-75"
                  }`}>
                    {/* Header: Rep + Fit Score */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {i === 0 && <CheckCircle className="h-3.5 w-3.5 text-primary" />}
                        <span className={`text-[12px] font-semibold ${i === 0 ? "text-primary" : ""}`}>{resolveRepLabel(s.rep)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[13px] font-bold ${
                          s.fitLabel === "Best Option" ? "text-success" :
                          s.fitLabel === "Good Option" ? "text-info" :
                          "text-muted-foreground"
                        }`}>{s.fitScore}</span>
                        <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${
                          s.fitLabel === "Best Option"
                            ? "bg-green-500/10 text-green-700 border-green-500/20"
                            : s.fitLabel === "Good Option"
                              ? "bg-blue-500/10 text-blue-700 border-blue-500/20"
                              : "bg-muted text-muted-foreground border-border"
                        }`}>
                          {s.fitLabel}
                        </Badge>
                      </div>
                    </div>

                    {/* Time + Distance */}
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {s.timeWindow}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> ZIP {dbAssistive.leadZip}</span>
                    </div>

                    {/* Microcopy */}
                    <p className="text-[10px] text-muted-foreground/80 italic">
                      Source win rate {s.sourceRate}% · Overall win rate {s.overallRate}%
                    </p>

                    {/* Signals */}
                    <p className="text-[9px] text-success font-medium flex items-center gap-1">
                      <Zap className="h-2.5 w-2.5" /> Derived from DB opportunities and outcomes
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Filter bar (Upcoming + Completed only) */}
        {activeTab !== "performance" && (
          <CockpitFilterBar
            search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search appointments..." />}
            right={!isRepRole ? (
              <Select value={repFilter} onValueChange={setRepFilter}>
                <SelectTrigger className="w-[180px] h-9"><SelectValue placeholder="All Reps" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  {reps.map((r) => <SelectItem key={r} value={r}>{resolveRepLabel(r)}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : undefined}
          />
        )}

        {/* Tab Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsContent value="upcoming" className="mt-4 space-y-3">
            {appointmentDataLoading ? (
              <EmptyState icon={RefreshCw} text="Loading latest appointments..." spinning />
            ) : upcomingPage.totalItems === 0 ? (
              <EmptyState icon={Calendar} text="No upcoming appointments" />
            ) : upcomingPage.paged.map((appt) => (
              <AppointmentCard key={appt.id} appt={appt} repLabel={resolveRepLabel(appt.assignedRep)}
                onView={() => setDetailId(appt.id)}
                onMarkOutcome={() => { setOutcomeDialog(appt.id); setSelectedOutcome("sold"); }}
                onReschedule={() => openRescheduleDialog(appt.id)}
                onCancel={() => handleCancel(appt.id)}
                onCreateFollowUp={() => { setFollowUpDialog(appt.id); }}
                onStartAppointment={() => navigate(`/opportunities/${appt.id}`)}
                onOpenConversation={() => navigate("/messages")}
                showActions
              />
            ))}
            <PaginationBar {...upcomingPage} />
          </TabsContent>

          <TabsContent value="completed" className="mt-4 space-y-3">
            {appointmentDataLoading ? (
              <EmptyState icon={RefreshCw} text="Loading latest appointments..." spinning />
            ) : completedPage.totalItems === 0 ? (
              <EmptyState icon={CheckCircle} text="No completed appointments" />
            ) : completedPage.paged.map((appt) => (
              <AppointmentCard key={appt.id} appt={appt} repLabel={resolveRepLabel(appt.assignedRep)}
                onView={() => setDetailId(appt.id)}
                onCreateFollowUp={appt.needsFollowUp ? () => setFollowUpDialog(appt.id) : undefined}
                onOpenConversation={() => navigate("/messages")}
              />
            ))}
            <PaginationBar {...completedPage} />
          </TabsContent>

          <TabsContent value="performance" className="mt-4">
            <PerformanceView appointments={appointments} reps={reps} resolveRepLabel={resolveRepLabel}
              funnel={conversionFunnel} repConversion={repConversion} sourceConversion={sourceConversion}
              rescheduleMetrics={rescheduleMetrics} />
          </TabsContent>
        </Tabs>

        {/* ── DETAIL SHEET ── */}
        <Sheet open={!!detailAppt} onOpenChange={() => setDetailId(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {detailAppt && (
              <AppointmentDetail appt={detailAppt} repLabel={resolveRepLabel(detailAppt.assignedRep)} conversation={detailConvo} recentMessages={detailMessages}
                onMarkOutcome={() => { setOutcomeDialog(detailAppt.id); setSelectedOutcome("sold"); setDetailId(null); }}
                onReschedule={() => { openRescheduleDialog(detailAppt.id); setDetailId(null); }}
                onCancel={() => { handleCancel(detailAppt.id); setDetailId(null); }}
                onStartAppointment={() => { navigate(`/opportunities/${detailAppt.id}`); setDetailId(null); }}
                onCreateFollowUp={() => { setFollowUpDialog(detailAppt.id); setDetailId(null); }}
              />
            )}
          </SheetContent>
        </Sheet>

        {/* ── ENFORCED OUTCOME FLOW (3-Step + Guided Next) ── */}
        <Dialog open={!!outcomeDialog} onOpenChange={() => resetOutcomeDialog()}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {outcomeStep === 4 ? (
                  <><CheckCircle className="h-5 w-5 text-success" /> What to do next</>
                ) : (
                  <><ClipboardList className="h-5 w-5 text-primary" /> Complete Appointment — Step {outcomeStep} of 3</>
                )}
              </DialogTitle>
              {outcomeStep < 4 && (
                <div className="flex gap-1 mt-2">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className={cn("h-1 flex-1 rounded-full transition-colors",
                      s <= outcomeStep ? "bg-primary" : "bg-muted"
                    )} />
                  ))}
                </div>
              )}
            </DialogHeader>

            {/* STEP 1: Select Outcome */}
            {outcomeStep === 1 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Select the appointment outcome</p>
                <div className="grid grid-cols-2 gap-2">
                  {(["sold", "no_sale", "rescheduled", "canceled", "no_show"] as AppointmentOutcome[]).map((o) => {
                    const cfg = OUTCOME_CONFIG[o];
                    const OIcon = cfg.icon;
                    return (
                      <button key={o} onClick={() => setSelectedOutcome(o)}
                        className={cn("flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all text-left",
                          selectedOutcome === o ? `${cfg.bg} border-2 font-medium` : "hover:bg-muted/50"
                        )}>
                        <OIcon className={cn("h-4 w-4", cfg.color)} /> {cfg.label}
                      </button>
                    );
                  })}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => resetOutcomeDialog()}>Cancel</Button>
                  <Button onClick={() => setOutcomeStep(2)}>Next — Add Notes <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
                </DialogFooter>
              </div>
            )}

            {/* STEP 2: Required Notes */}
            {outcomeStep === 2 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Document what happened during this appointment</p>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className={cn("gap-1", OUTCOME_CONFIG[selectedOutcome].color)}>
                    {(() => { const OIcon = OUTCOME_CONFIG[selectedOutcome].icon; return <OIcon className="h-3 w-3" />; })()}
                    {OUTCOME_CONFIG[selectedOutcome].label}
                  </Badge>
                </div>
                <Textarea
                  value={outcomeNotes}
                  onChange={(e) => setOutcomeNotes(e.target.value)}
                  placeholder="What happened? Key discussion points, customer feedback, decisions made..."
                  rows={4}
                  className="text-sm"
                />
                {!outcomeNotes.trim() && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Notes are required to proceed
                  </p>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOutcomeStep(1)}>Back</Button>
                  <Button onClick={() => setOutcomeStep(3)} disabled={!outcomeNotes.trim()}>
                    Next — Sales QA <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* STEP 3: Sales QA Checklist */}
            {outcomeStep === 3 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Confirm what was covered during this appointment</p>
                <div className="space-y-1">
                  {SALES_QA_ITEMS.map((item) => {
                    const checked = salesQA[item.key];
                    return (
                      <button key={item.key}
                        onClick={() => setSalesQA((prev) => ({ ...prev, [item.key]: !prev[item.key] }))}
                        className={cn("w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all text-sm",
                          checked ? "bg-success/10 border-success/30" : "hover:bg-muted/50"
                        )}>
                        <div className={cn("h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
                          checked ? "bg-success border-success/50" : "border-muted-foreground/30"
                        )}>
                          {checked && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn("font-medium", checked && "text-success")}>{item.label}</p>
                          <p className="text-[11px] text-muted-foreground">{item.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                  <span>{Object.values(salesQA).filter(Boolean).length}/{SALES_QA_ITEMS.length} completed</span>
                  <Progress value={(Object.values(salesQA).filter(Boolean).length / SALES_QA_ITEMS.length) * 100} className="h-1.5 w-24" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOutcomeStep(2)}>Back</Button>
                  <Button onClick={handleMarkOutcome}>
                    <CheckCircle className="h-3.5 w-3.5 mr-1" /> Submit & Complete
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* STEP 4: Guided Next Steps */}
            {outcomeStep === 4 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={cn("gap-1", OUTCOME_CONFIG[selectedOutcome].color)}>
                    {(() => { const OIcon = OUTCOME_CONFIG[selectedOutcome].icon; return <OIcon className="h-3 w-3" />; })()}
                    {OUTCOME_CONFIG[selectedOutcome].label}
                  </Badge>
                  <span className="text-xs text-muted-foreground">Outcome recorded</span>
                </div>
                <p className="text-sm text-muted-foreground">Based on this outcome, here's what to do next:</p>
                <div className="space-y-1.5">
                  {getGuidedNextSteps(selectedOutcome, salesQA).map((step, i) => {
                    const SIcon = step.icon;
                    return (
                      <button key={i}
                        onClick={() => {
                          if (step.action === "follow_up") {
                            setFollowUpDialog(outcomeDialog);
                            resetOutcomeDialog();
                          } else if (step.action === "reschedule" && outcomeDialog) {
                            openRescheduleDialog(outcomeDialog);
                            resetOutcomeDialog();
                          } else {
                            toast.success(`${step.label} — action initiated`);
                            handleOutcomeComplete();
                          }
                        }}
                        className={cn("w-full flex items-center gap-3 px-3 py-3 rounded-lg border text-left transition-all hover:bg-muted/50",
                          step.priority === "high" && "border-primary/30 bg-primary/5",
                        )}>
                        <SIcon className={cn("h-5 w-5 shrink-0",
                          step.priority === "high" ? "text-primary" : "text-muted-foreground"
                        )} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{step.label}</p>
                          <p className="text-[11px] text-muted-foreground">{step.description}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      </button>
                    );
                  })}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => handleOutcomeComplete()}>Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ── RESCHEDULE DIALOG ── */}
        <Dialog open={!!rescheduleDialog} onOpenChange={(open) => { if (!open) setRescheduleDialog(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Reschedule Appointment</DialogTitle>
              <p className="text-xs text-muted-foreground">Select the new appointment date and time before saving.</p>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">New Date</p>
                <Input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">New Time</p>
                <Input
                  type="time"
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                />
              </div>
              {rescheduleConflict && (
                <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-warning">
                  Conflict: {rescheduleConflict.assignedRep || "Assigned rep"} is already booked for {rescheduleConflict.primaryName} within the 2-hour appointment + 2-hour buffer window.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRescheduleDialog(null)}>Cancel</Button>
              <Button onClick={handleConfirmReschedule} disabled={!rescheduleDate || !rescheduleTime || !!rescheduleConflict}>
                Save Reschedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── FOLLOW-UP DIALOG ── */}
        <Dialog open={!!followUpDialog} onOpenChange={() => setFollowUpDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Create Follow-Up</DialogTitle>
              <p className="text-xs text-muted-foreground">This will update the pipeline stage to Follow-Up Needed</p>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Reason</p>
                <Select value={followUpReason} onValueChange={setFollowUpReason}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Post-appointment check-in", "No show — re-engage", "Price objection recovery", "Customer requested callback", "Reschedule needed", "Manager escalation"].map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Textarea value={followUpNotes} onChange={(e) => setFollowUpNotes(e.target.value)} placeholder="Additional context..." rows={2} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFollowUpDialog(null)}>Cancel</Button>
              <Button onClick={handleCreateFollowUp}>Create Follow-Up</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CockpitPage>
    </TooltipProvider>
  );
}

/* ═══════ APPOINTMENT CARD ═══════ */

function AppointmentCard({
  appt, repLabel, onView, onMarkOutcome, onReschedule, onCancel, onCreateFollowUp, onStartAppointment, onOpenConversation, showActions,
}: {
  appt: EnrichedAppointment;
  repLabel: string;
  onView: () => void;
  onMarkOutcome?: () => void;
  onReschedule?: () => void;
  onCancel?: () => void;
  onCreateFollowUp?: () => void;
  onStartAppointment?: () => void;
  onOpenConversation?: () => void;
  showActions?: boolean;
}) {
  const cfg = OUTCOME_CONFIG[appt.outcome];
  const Icon = cfg.icon;
  const apptDate = appt.appointmentTime
    ? parseISO(appt.appointmentTime)
    : (appt.updatedAt ? parseISO(appt.updatedAt) : (appt.createdAt ? parseISO(appt.createdAt) : null));
  const time = apptDate ? format(apptDate, "MMM d, h:mm a") : "—";
  const isUpcoming = appt.outcome === "upcoming";
  const isNeedsOutcome =
    appt.outcome === "needs_outcome" ||
    (isUpcoming && !!apptDate && apptDate.getTime() < Date.now());
  const isTodays = !!apptDate && isToday(apptDate);

  return (
    <Card className={cn(
      "shadow-none border border-border/60 rounded-lg transition-colors hover:border-border hover:bg-muted/20",
      isTodays && isUpcoming && "border-l-2 border-l-primary",
      isNeedsOutcome && "border-l-2 border-l-warning",
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <p className="text-sm font-semibold text-foreground">{appt.primaryName}</p>
              <Badge variant="outline" className={cn("gap-1 text-[10px] h-5 font-normal bg-transparent border-border/60", cfg.color)}>
                <Icon className="h-3 w-3" /> {cfg.label}
              </Badge>
              {isTodays && isUpcoming && <Badge variant="outline" className="text-[10px] h-5 font-normal bg-transparent border-primary/40 text-primary">Today</Badge>}
              {appt.isHighValue && <Badge variant="outline" className="text-[10px] h-5 font-normal bg-transparent border-primary/30 text-primary gap-0.5"><DollarSign className="h-3 w-3" />High Value</Badge>}
              {appt.needsFollowUp && <Badge variant="outline" className="text-[10px] h-5 font-normal bg-transparent border-warning/40 text-warning">Follow-Up Required</Badge>}
              {(() => { const tc = TEMP_CONFIG[appt.temperature]; return (
                <Badge variant="outline" className={cn("text-[10px] h-5 gap-0.5 font-normal bg-transparent border-border/60", tc.color)}>{tc.icon} {tc.label}</Badge>
              ); })()}
              {isUpcoming && (() => { const rc = RISK_CONFIG[appt.protection.noShowRisk]; return (
                <Badge variant="outline" className={cn("text-[10px] h-5 gap-0.5 font-normal bg-transparent border-border/60", rc.color)}>
                  <Shield className="h-3 w-3" /> {rc.label}
                </Badge>
              ); })()}
              {isUpcoming && appt.protection.isProtected && (
                <Badge variant="outline" className="text-[10px] h-5 gap-0.5 font-normal bg-transparent text-success border-success/30">
                  <Bell className="h-3 w-3" /> Reminders Sent
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {time}</span>
              <span className="flex items-center gap-1"><User className="h-3 w-3" /> {repLabel}</span>
              <span className="flex items-center gap-1"><Target className="h-3 w-3" /> {appt.projectType}</span>
              {appt.totalEstimate > 0 && <span className="font-medium text-foreground">${appt.totalEstimate.toLocaleString()}</span>}
            </div>
          </div>

           {/* Actions */}
           <div className="flex items-center gap-1 shrink-0 flex-wrap">
             {showActions && (isUpcoming || isNeedsOutcome) && onStartAppointment && (
               <Tooltip><TooltipTrigger asChild>
                 <Button size="sm" className="h-7 text-xs gap-1" onClick={onStartAppointment}><Play className="h-3 w-3" /> {appt.hasConsultationSession ? "Resume" : "Start"}</Button>
               </TooltipTrigger><TooltipContent>Open sales presentation</TooltipContent></Tooltip>
             )}
            {(showActions || isNeedsOutcome) && onMarkOutcome && (
              <Tooltip><TooltipTrigger asChild>
                <Button size="sm" variant={isNeedsOutcome ? "default" : "outline"} className={cn("h-7 text-xs", isNeedsOutcome && "bg-warning hover:bg-warning")} onClick={onMarkOutcome}>
                  Mark Outcome
                </Button>
              </TooltipTrigger><TooltipContent>Record appointment result</TooltipContent></Tooltip>
            )}
            {showActions && onReschedule && (
              <Tooltip><TooltipTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onReschedule}><RotateCcw className="h-3 w-3" /></Button>
              </TooltipTrigger><TooltipContent>Reschedule appointment</TooltipContent></Tooltip>
            )}
            {onCreateFollowUp && (
              <Tooltip><TooltipTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-warning hover:text-warning border-border/60 hover:bg-warning/5" onClick={onCreateFollowUp}>
                  <Plus className="h-3 w-3" /> Follow-Up
                </Button>
              </TooltipTrigger><TooltipContent>Create follow-up task</TooltipContent></Tooltip>
            )}
            {onOpenConversation && (
              <Tooltip><TooltipTrigger asChild>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onOpenConversation}><MessageSquare className="h-3.5 w-3.5" /></Button>
              </TooltipTrigger><TooltipContent>Open conversation</TooltipContent></Tooltip>
            )}
            <Tooltip><TooltipTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onView}><Eye className="h-3.5 w-3.5" /></Button>
            </TooltipTrigger><TooltipContent>View details</TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild>
              <Link to={`/opportunities/${appt.id}`}><Button size="icon" variant="ghost" className="h-7 w-7"><ArrowRight className="h-3.5 w-3.5" /></Button></Link>
            </TooltipTrigger><TooltipContent>Open customer profile</TooltipContent></Tooltip>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════ DETAIL PANEL ═══════ */

function AppointmentDetail({
  appt, repLabel, conversation, recentMessages, onMarkOutcome, onReschedule, onCancel, onStartAppointment, onCreateFollowUp,
}: {
  appt: EnrichedAppointment; repLabel: string; conversation: any; recentMessages: any[];
  onMarkOutcome: () => void; onReschedule: () => void; onCancel: () => void;
  onStartAppointment: () => void; onCreateFollowUp: () => void;
}) {
  const cfg = OUTCOME_CONFIG[appt.outcome];
  const Icon = cfg.icon;
  const isUpcoming = appt.outcome === "upcoming";
  const apptDate = appt.appointmentTime
    ? parseISO(appt.appointmentTime)
    : (appt.updatedAt ? parseISO(appt.updatedAt) : (appt.createdAt ? parseISO(appt.createdAt) : null));
  const isNeedsOutcome =
    appt.outcome === "needs_outcome" ||
    (isUpcoming && !!apptDate && apptDate.getTime() < Date.now());

  return (
    <>
      <SheetHeader><SheetTitle className="text-lg">{appt.primaryName}</SheetTitle></SheetHeader>
      <div className="mt-4 space-y-5">
        {/* Status Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={cn("gap-1", cfg.color)}><Icon className="h-3.5 w-3.5" /> {cfg.label}</Badge>
          <Badge variant="outline">{appt.stage}</Badge>
          {appt.isHighValue && <Badge variant="outline" className="gap-0.5 border-primary/30 text-primary"><DollarSign className="h-3 w-3" /> High Value</Badge>}
          {appt.needsFollowUp && <Badge className="bg-warning/60 text-[11px]">Follow-Up Required</Badge>}
        </div>

        {/* Quick Info */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><p className="text-[11px] text-muted-foreground">Date & Time</p><p className="font-medium">{apptDate ? format(apptDate, "MMM d, yyyy h:mm a") : "—"}</p></div>
          <div><p className="text-[11px] text-muted-foreground">Rep</p><p className="font-medium">{repLabel}</p></div>
          <div><p className="text-[11px] text-muted-foreground">Project</p><p className="font-medium">{appt.projectType}</p></div>
          <div><p className="text-[11px] text-muted-foreground">Value</p><p className="font-medium">{appt.totalEstimate > 0 ? `$${appt.totalEstimate.toLocaleString()}` : "—"}</p></div>
        </div>

        {/* Contact */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Contact</p>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /> {appt.primaryPhone}</div>
            {appt.primaryEmail && <div className="flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5 text-muted-foreground" /> {appt.primaryEmail}</div>}
            {appt.address && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {appt.address}, {appt.city}, {appt.state} {appt.zip}</div>}
          </div>
        </div>

        <Separator />

        {/* Linked Records */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Linked Records</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/opportunities/${appt.id}`}><Badge variant="outline" className="gap-1 cursor-pointer hover:bg-muted"><Target className="h-3 w-3" /> Lead / Opportunity</Badge></Link>
            {appt.linkedJobId && <Link to={`/jobs/${appt.linkedJobId}`}><Badge variant="outline" className="gap-1 cursor-pointer hover:bg-muted"><FileText className="h-3 w-3" /> Job</Badge></Link>}
            {conversation && <Link to="/messages"><Badge variant="outline" className="gap-1 cursor-pointer hover:bg-muted"><MessageSquare className="h-3 w-3" /> Conversation</Badge></Link>}
          </div>
        </div>

        {/* Recent Messages */}
        {recentMessages.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Recent Messages</p>
            <div className="space-y-1.5">
              {recentMessages.map((m) => (
                <div key={m.id} className="text-xs border rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-medium">{m.sender}</span>
                    <span className="text-muted-foreground">{format(parseISO(m.timestamp), "MMM d, h:mm a")}</span>
                  </div>
                  <p className="text-muted-foreground line-clamp-2">{m.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Protection Status */}
        {appt.outcome === "upcoming" && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><Shield className="h-3.5 w-3.5" /> Protection Status</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">No-Show Risk</span>
                <Badge variant="outline" className={cn("text-[9px]", RISK_CONFIG[appt.protection.noShowRisk].color, RISK_CONFIG[appt.protection.noShowRisk].bg)}>
                  {RISK_CONFIG[appt.protection.noShowRisk].label}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Progress value={appt.protection.protectionScore} className="h-1.5 flex-1" />
                <span className="text-xs font-medium">{appt.protection.protectionScore}%</span>
              </div>
              {appt.protection.riskFactors.length > 0 && (
                <div className="space-y-0.5">
                  {appt.protection.riskFactors.map((f, i) => (
                    <p key={i} className="text-[10px] text-warning flex items-center gap-1"><AlertTriangle className="h-2.5 w-2.5" /> {f}</p>
                  ))}
                </div>
              )}
              <div className="space-y-1">
                <p className="text-[10px] font-medium text-muted-foreground">Reminders</p>
                {appt.protection.reminders.map((r) => (
                  <div key={r.id} className={cn("flex items-center gap-2 text-[10px] px-2 py-1 rounded",
                    r.status === "sent" && "bg-success/10 text-success",
                    r.status === "confirmed" && "bg-success/10 text-success font-medium",
                    r.status === "pending" && "text-muted-foreground",
                  )}>
                    {r.status === "sent" ? <CheckCircle className="h-3 w-3" /> : r.status === "confirmed" ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    <span className="flex-1">{r.label}</span>
                    <span className="uppercase text-[8px] opacity-60">{r.channel}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Recovery Actions (for detected no-shows) */}
        {appt.noShowDetection?.isNoShow && appt.recoveryActions.length > 0 && (
          <div>
            <p className="text-xs font-medium text-destructive mb-2 flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> Recovery Actions</p>
            <div className="space-y-1.5">
              {appt.recoveryActions.map((action) => (
                <button key={action.id} className="w-full text-left border rounded-lg px-3 py-2 hover:bg-muted/50 transition-colors"
                  onClick={() => toast.success(`${action.label} executed`)}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-foreground">{action.label}</p>
                    <Badge variant="outline" className={cn("text-[8px] h-4",
                      action.priority === "immediate" && "text-destructive border-destructive/30",
                      action.priority === "within_1h" && "text-warning border-warning/30",
                    )}>{action.priority}</Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{action.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <Separator />

        {/* Activity History */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">History</p>
          <div className="space-y-2">
            {appt.activities.slice(-8).reverse().map((act) => (
              <div key={act.id} className="flex items-start gap-2 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 mt-1.5 shrink-0" />
                <div>
                  <p className="text-foreground">{act.description}</p>
                  <p className="text-muted-foreground">{act.createdBy} · {format(parseISO(act.createdAt), "MMM d")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

         {/* Actions */}
         <div className="space-y-2">
           {(isUpcoming || isNeedsOutcome) && (
             <div className="flex items-center gap-2">
               {(isUpcoming || isNeedsOutcome) && <Button className="flex-1 gap-1" onClick={onStartAppointment}><Play className="h-4 w-4" /> {appt.hasConsultationSession ? "Resume" : "Start"} Appointment</Button>}
              <Button variant={isNeedsOutcome ? "default" : "outline"} className={cn(isNeedsOutcome && "bg-warning hover:bg-warning flex-1")} onClick={onMarkOutcome}>
                {isNeedsOutcome ? "Mark Outcome Now" : "Mark Outcome"}
              </Button>
              {isUpcoming && <Button variant="outline" onClick={onReschedule}><RotateCcw className="h-4 w-4" /></Button>}
              {isUpcoming && <Button variant="ghost" className="text-muted-foreground" onClick={onCancel}><Ban className="h-4 w-4" /></Button>}
            </div>
          )}
          {appt.needsFollowUp && (
            <Button variant="outline" className="w-full gap-1 border-warning/40 text-warning hover:bg-warning/10" onClick={onCreateFollowUp}>
              <Plus className="h-4 w-4" /> Create Follow-Up
            </Button>
          )}
          <div className="flex gap-2">
            <Link to="/messages" className="flex-1"><Button variant="outline" className="w-full gap-1 text-xs"><MessageSquare className="h-3.5 w-3.5" /> Messages</Button></Link>
            <Link to={`/opportunities/${appt.id}`} className="flex-1"><Button variant="outline" className="w-full gap-1 text-xs"><User className="h-3.5 w-3.5" /> Profile</Button></Link>
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════ PERFORMANCE VIEW ═══════ */

function PerformanceView({ appointments, reps, resolveRepLabel, funnel, repConversion, sourceConversion, rescheduleMetrics }: {
  appointments: EnrichedAppointment[]; reps: string[];
  resolveRepLabel: (rep: string) => string;
  funnel: ConversionFunnel; repConversion: RepConversionMetrics[]; sourceConversion: SourceConversionMetrics[];
  rescheduleMetrics: RescheduleMetrics;
}) {
  const totalRevenue = repConversion.reduce((s, r) => s + r.totalRevenue, 0);

  return (
    <div className="space-y-6">
      {/* Reschedule & Recovery Metrics */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-[12px] font-bold text-foreground uppercase tracking-wider">Appointment Protection & Recovery</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold text-warning">{rescheduleMetrics.totalNoShows}</p>
            <p className="text-[11px] text-muted-foreground">Total No-Shows</p>
          </div>
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold text-warning">{rescheduleMetrics.rescheduled}</p>
            <p className="text-[11px] text-muted-foreground">Rescheduled ({rescheduleMetrics.rescheduleRate}%)</p>
          </div>
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold text-success">{rescheduleMetrics.recovered}</p>
            <p className="text-[11px] text-muted-foreground">Recovered ({rescheduleMetrics.recoveryRate}%)</p>
          </div>
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold text-success">${rescheduleMetrics.recoveredRevenue.toLocaleString()}</p>
            <p className="text-[11px] text-muted-foreground">Recovered Revenue</p>
          </div>
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold text-destructive">${rescheduleMetrics.lostRevenue.toLocaleString()}</p>
            <p className="text-[11px] text-muted-foreground">Lost Revenue</p>
          </div>
        </div>
        {rescheduleMetrics.totalNoShows > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden flex">
              <div className="bg-success/60 h-full" style={{ width: `${rescheduleMetrics.recoveryRate}%` }} />
              <div className="bg-warning/40 h-full" style={{ width: `${rescheduleMetrics.rescheduleRate - rescheduleMetrics.recoveryRate}%` }} />
            </div>
            <span className="text-xs text-muted-foreground">{rescheduleMetrics.recoveryRate}% recovered</span>
          </div>
        )}
      </section>

      {/* Conversion Funnel */}
      <section className="space-y-3 pt-4 border-t border-border/60">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-[12px] font-bold text-foreground uppercase tracking-wider">Conversion Funnel</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold">{funnel.booked}</p>
            <p className="text-[11px] text-muted-foreground">Booked</p>
          </div>
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold">{funnel.showRate}%</p>
            <p className="text-[11px] text-muted-foreground">Show Rate</p>
          </div>
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold">{funnel.completed}</p>
            <p className="text-[11px] text-muted-foreground">Completed</p>
          </div>
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold text-success">{funnel.sold}</p>
            <p className="text-[11px] text-muted-foreground">Sold</p>
          </div>
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold text-primary">{funnel.overallConversionRate}%</p>
            <p className="text-[11px] text-muted-foreground">Overall Conv.</p>
          </div>
          <div className="text-center border border-border/60 rounded-md p-3">
            <p className="text-xl font-bold">{funnel.noSaleToFollowUpRate}%</p>
            <p className="text-[11px] text-muted-foreground">No Sale → F/U</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden flex">
            <div className="bg-success/60 h-full" style={{ width: `${funnel.overallConversionRate}%` }} />
            <div className="bg-warning/40 h-full" style={{ width: `${funnel.bookToCompleteRate - funnel.overallConversionRate}%` }} />
          </div>
          <span className="text-xs text-muted-foreground">{funnel.overallConversionRate}% converted</span>
        </div>
      </section>

      {/* Rep Performance */}
      <section className="space-y-3 pt-4 border-t border-border/60">
        <div className="flex items-center gap-2">
          <UserCheck className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-[12px] font-bold text-foreground uppercase tracking-wider">Rep Conversion Performance</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border/60 text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium">Rep</th>
              <th className="px-3 py-2 font-medium text-center">Booked</th>
              <th className="px-3 py-2 font-medium text-center">Sold</th>
              <th className="px-3 py-2 font-medium text-center">Close Rate</th>
              <th className="px-3 py-2 font-medium text-center">Show Rate</th>
              <th className="px-3 py-2 font-medium text-center">Avg Response</th>
              <th className="px-3 py-2 font-medium text-center">F/U Rate</th>
              <th className="px-3 py-2 font-medium text-right">Revenue</th>
            </tr></thead>
            <tbody className="divide-y divide-border/60">
              {repConversion.map((r) => (
                <tr key={r.rep} className="hover:bg-muted/30">
                  <td className="px-3 py-2.5 font-medium">{resolveRepLabel(r.rep)}</td>
                  <td className="px-3 py-2.5 text-center">{r.booked}</td>
                  <td className="px-3 py-2.5 text-center text-success font-medium">{r.sold}</td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center gap-2 justify-center">
                      <Progress value={r.closeRate} className="h-1.5 w-14" />
                      <span className={cn("text-xs font-medium", r.closeRate >= 30 ? "text-success" : "text-warning")}>{r.closeRate}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center"><span className={cn("text-xs", r.showRate >= 80 ? "text-success" : "text-warning")}>{r.showRate}%</span></td>
                  <td className="px-3 py-2.5 text-center text-xs">
                    {r.avgResponseTimeSec !== null ? (r.avgResponseTimeSec < 300 ? <span className="text-success">{Math.round(r.avgResponseTimeSec / 60)}m</span> : <span className="text-warning">{Math.round(r.avgResponseTimeSec / 60)}m</span>) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-center"><span className={cn("text-xs", r.followUpCompletionRate >= 80 ? "text-success" : "text-warning")}>{r.followUpCompletionRate}%</span></td>
                  <td className="px-3 py-2.5 text-right font-medium">${r.totalRevenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Source Conversion Performance */}
      <section className="space-y-3 pt-4 border-t border-border/60">
        <div className="flex items-center gap-2">
          <Target className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-[12px] font-bold text-foreground uppercase tracking-wider">Source Conversion Performance</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border/60 text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium">Source</th>
              <th className="px-3 py-2 font-medium text-center">Leads</th>
              <th className="px-3 py-2 font-medium text-center">Appts</th>
              <th className="px-3 py-2 font-medium text-center">Sold</th>
              <th className="px-3 py-2 font-medium text-center">$/Lead</th>
              <th className="px-3 py-2 font-medium text-center">$/Appt</th>
              <th className="px-3 py-2 font-medium text-center">$/Sale</th>
              <th className="px-3 py-2 font-medium text-center">Conv.</th>
              <th className="px-3 py-2 font-medium text-center">ROI</th>
              <th className="px-3 py-2 font-medium text-right">Revenue</th>
            </tr></thead>
            <tbody className="divide-y divide-border/60">
              {sourceConversion.map((s) => (
                <tr key={s.source} className="hover:bg-muted/30">
                  <td className="px-3 py-2.5 font-medium">{s.source}</td>
                  <td className="px-3 py-2.5 text-center">{s.totalLeads}</td>
                  <td className="px-3 py-2.5 text-center">{s.appointments}</td>
                  <td className="px-3 py-2.5 text-center text-success font-medium">{s.sold}</td>
                  <td className="px-3 py-2.5 text-center text-xs">${s.costPerLead}</td>
                  <td className="px-3 py-2.5 text-center text-xs">${s.costPerAppointment}</td>
                  <td className="px-3 py-2.5 text-center text-xs">{s.costPerSale > 0 ? `$${s.costPerSale}` : "—"}</td>
                  <td className="px-3 py-2.5 text-center"><span className={cn("text-xs font-medium", s.overallRate >= 20 ? "text-success" : "text-muted-foreground")}>{s.overallRate}%</span></td>
                  <td className="px-3 py-2.5 text-center"><span className={cn("text-xs font-medium", s.roi > 200 ? "text-success" : s.roi > 0 ? "text-warning" : "text-destructive")}>{s.roi}%</span></td>
                  <td className="px-3 py-2.5 text-right font-medium">${s.totalRevenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Outcome Distribution */}
      <section className="space-y-3 pt-4 border-t border-border/60">
        <p className="text-[12px] font-bold text-foreground uppercase tracking-wider">Outcome Distribution</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {(["sold", "no_sale", "no_show", "canceled", "rescheduled"] as AppointmentOutcome[]).map((o) => {
            const cfg = OUTCOME_CONFIG[o];
            const OIcon = cfg.icon;
            const count = appointments.filter((a) => a.outcome === o).length;
            const pct = appointments.length > 0 ? Math.round((count / appointments.length) * 100) : 0;
            return (
              <div key={o} className="border border-border/60 rounded-md p-3 text-center">
                <OIcon className={cn("h-5 w-5 mx-auto mb-1", cfg.color)} />
                <p className="text-lg font-bold">{count}</p>
                <p className="text-[11px] text-muted-foreground">{cfg.label} ({pct}%)</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/* ═══════ SHARED ═══════ */

function KPI({ label, value, icon: Icon, accent }: { label: string; value: string | number; icon: React.ElementType; accent?: string }) {
  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-3">
        <Icon className={cn("h-5 w-5 text-muted-foreground", accent)} />
        <div>
          <p className={cn("text-lg font-bold", accent)}>{value}</p>
          <p className="text-[11px] text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ icon: Icon, text, spinning = false }: { icon: React.ElementType; text: string; spinning?: boolean }) {
  return (
    <div className="py-12 text-center text-muted-foreground">
      <Icon className={cn("h-8 w-8 mx-auto mb-2 opacity-30", spinning && "animate-spin")} />
      <p className="text-sm">{text}</p>
    </div>
  );
}
